import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { deleteCalendarEventsForScheduleItems } from '@/lib/deleteCalendarEvents';
import { paginateQuery } from '@/lib/paginateSupabase';
import { useAuditorCohortIds } from '@/hooks/useAuditorCohortIds';

// ── Types ──

export interface CohortRow {
  id: string;
  name: string;
  program_id: string;
  organization_id: string | null;
  start_date: string | null;
  end_date: string | null;
  demo_day_date: string | null;
  demo_day_start_hour: number | null;
  demo_day_end_hour: number | null;
  demo_day_link: string | null;
  demo_day_enabled: boolean;
  project_deadline: string | null;
  project_deadline_week: number | null;
  challenge_lab_deadline: string | null;
  challenge_lab_deadline_week: number | null;
  content_access_deadline: string | null;
  status: string | null;
  is_public: boolean;
  price: number | null;
  currency: string | null;
  enrollment_open: boolean | null;
  meet_link: string | null;
  created_at: string;
  is_perpetual: boolean;
  demo_day_recording_link: string | null;
  demo_day_transcript_url: string | null;
  results_report_url: string | null;
  results_report_name: string | null;
  results_report_uploaded_at: string | null;
  // joined
  program_name: string | null;
  program_modality: string | null;
  program_category: string | null;
  organization_name: string | null;
  participants_count: number;
  // agregados para la grilla
  certified_count: number;
  max_week: number | null;
  sessions_missing_recording: number;
  missing_recording_samples: { title: string; date: string | null }[];
}

export interface ScheduleItemRow {
  id: string;
  cohort_id: string;
  type: string | null;
  ref_id: string | null;
  title: string | null;
  description: string | null;
  week_number: number | null;
  scheduled_date: string | null;
  start_hour: number | null;
  start_minute: number | null;
  end_hour: number | null;
  end_minute: number | null;
  connection_link: string | null;
  expert_id: string | null;
  recording_link: string | null;
  meet_recording_link: string | null;
}


export interface CohortParticipantRow {
  id: string;
  cohort_id: string;
  user_id: string;
  enrolled_at: string | null;
  full_name: string;
  email: string;
  area: string | null;
}

export interface CohortMentorRow {
  id: string;
  cohort_id: string;
  user_id: string;
  full_name: string;
  email: string;
}

export interface ExpertRow {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
  bio: string | null;
  linkedin_url: string | null;
}

export interface ProgramRow {
  id: string;
  name: string;
  slug: string | null;
  modality?: string | null;
}

// ── Hook ──

export function useCohortManagement(selectedOrg: string) {
  const { currentRole, profile } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = currentRole === 'super_admin';
  const isAdmin = currentRole === 'admin';
  // Auditor: solo lectura. Elige empresa libremente (RLS lo limita a sus cohortes
  // asignadas). Sus queries se habilitan igual que admin.
  const isAuditor = currentRole === 'auditor';
  const canSelectOrg = isSuperAdmin || isAuditor;
  const userOrgId = profile?.organization_id;

  // Effective org for filtering
  const effectiveOrg = canSelectOrg ? selectedOrg : (userOrgId || '');
  const showAllOrgs = canSelectOrg && selectedOrg === 'all';

  // Invalida todas las queries que consumen datos de cohorts.
  // Necesario para que el calendario del participante refresque cuando el admin
  // cambia demo_day_date, project_deadline, etc.
  const invalidateAllCohortConsumers = () => {
    queryClient.invalidateQueries({ queryKey: ['cohort-management', 'cohorts'] });
    queryClient.invalidateQueries({ queryKey: ['all-cohort-enrollments'] });
    queryClient.invalidateQueries({ queryKey: ['cohort-enrollment'] });
  };

  // ── Cohorts ──
  const cohortsQuery = useQuery({
    queryKey: ['cohort-management', 'cohorts'],
    queryFn: async (): Promise<CohortRow[]> => {
      const { data, error } = await supabase
        .from('cohorts')
        .select('*, programs(name, modality, category), organizations(name)')
        .order('start_date', { ascending: false });
      if (error) throw error;

      // Paginado: sin esto, PostgREST corta en 1000 filas y las cohortes cuyas
      // filas de cohort_participants caen fuera de las primeras 1000 cuentan 0
      // (bug: cohorte recién creada mostraba 0 participantes teniendo 1).
      const counts = await paginateQuery<{ cohort_id: string }>((from, to) =>
        supabase.from('cohort_participants').select('cohort_id').range(from, to),
      );

      const countMap: Record<string, number> = {};
      (counts || []).forEach((r: any) => {
        countMap[r.cohort_id] = (countMap[r.cohort_id] || 0) + 1;
      });

      // Certificados emitidos por cohorte
      const certs = await paginateQuery<{ cohort_id: string }>((from, to) =>
        supabase.from('certificate_logs').select('cohort_id').range(from, to),
      );
      const certMap: Record<string, number> = {};
      (certs || []).forEach((r: any) => {
        if (r.cohort_id) certMap[r.cohort_id] = (certMap[r.cohort_id] || 0) + 1;
      });

      // Sesiones en vivo: semana máxima del calendario y sesiones pasadas sin grabación
      const today = new Date().toISOString().slice(0, 10);
      const items = await paginateQuery<any>((from, to) =>
        (supabase as any)
          .from('cohort_schedule_items')
          .select('cohort_id, title, type, week_number, scheduled_date, meet_recording_link')
          .range(from, to),
      );
      const maxWeekMap: Record<string, number> = {};
      const missingMap: Record<string, { title: string; date: string | null }[]> = {};
      (items || []).forEach((it: any) => {
        if (it.week_number != null) {
          maxWeekMap[it.cohort_id] = Math.max(maxWeekMap[it.cohort_id] || 0, it.week_number);
        }
        const isLive = ['workshop', 'mentoring', 'mentorship'].includes(it.type);
        const past = it.scheduled_date && it.scheduled_date < today;
        if (isLive && past && !(it.meet_recording_link ?? '').trim()) {
          (missingMap[it.cohort_id] ||= []).push({ title: it.title || 'Sesión', date: it.scheduled_date });
        }
      });
      Object.values(missingMap).forEach(list =>
        list.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? '')),
      );

      return (data || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        program_id: c.program_id,
        organization_id: c.organization_id,
        start_date: c.start_date,
        end_date: c.end_date,
        demo_day_date: c.demo_day_date,
        demo_day_start_hour: c.demo_day_start_hour,
        demo_day_end_hour: c.demo_day_end_hour,
        demo_day_link: c.demo_day_link,
        demo_day_enabled: c.demo_day_enabled ?? true,
        demo_day_recording_link: c.demo_day_recording_link ?? null,
        demo_day_transcript_url: c.demo_day_transcript_url ?? null,
        results_report_url: c.results_report_url ?? null,
        results_report_name: c.results_report_name ?? null,
        results_report_uploaded_at: c.results_report_uploaded_at ?? null,
        project_deadline: c.project_deadline ?? null,
        project_deadline_week: c.project_deadline_week ?? null,
        challenge_lab_deadline: c.challenge_lab_deadline ?? null,
        challenge_lab_deadline_week: c.challenge_lab_deadline_week ?? null,
        content_access_deadline: c.content_access_deadline ?? null,
        status: c.status,
        is_public: c.is_public,
        price: c.price,
        currency: c.currency,
        enrollment_open: c.enrollment_open,
        meet_link: c.meet_link ?? null,
        created_at: c.created_at,
        is_perpetual: c.is_perpetual ?? false,
        program_name: c.programs?.name || null,
        program_modality: c.programs?.modality || null,
        program_category: c.programs?.category || null,
        organization_name: c.organizations?.name || null,
        participants_count: countMap[c.id] || 0,
        certified_count: certMap[c.id] || 0,
        max_week: maxWeekMap[c.id] ?? null,
        sessions_missing_recording: (missingMap[c.id] || []).length,
        missing_recording_samples: (missingMap[c.id] || []).slice(0, 3),
      }));
    },
    enabled: isSuperAdmin || isAdmin || isAuditor,
  });

  // El auditor debe ver SOLO sus cohortes asignadas (la RLS también expone las
  // públicas, así que filtramos por el set asignado).
  const { cohortIds: auditorCohortIds } = useAuditorCohortIds();
  const filteredCohorts = (cohortsQuery.data || []).filter(c => {
    if (isAuditor && !auditorCohortIds.includes(c.id)) return false;
    if (showAllOrgs) return true;
    return c.organization_id === effectiveOrg;
  });

  // ── Programs ──
  const programsQuery = useQuery({
    queryKey: ['cohort-management', 'programs'],
    queryFn: async (): Promise<ProgramRow[]> => {
      const { data, error } = await supabase
        .from('programs')
        .select('id, name, slug, status, modality')
        .in('status', ['active', 'public'])
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: isSuperAdmin || isAdmin || isAuditor,
  });

  // ── Experts (users with mentor or super_admin role) ──
  const expertsQuery = useQuery({
    queryKey: ['cohort-management', 'experts'],
    queryFn: async (): Promise<ExpertRow[]> => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('user_id, role, profiles(id, full_name, email, avatar_url, bio, linkedin_url)')
        .in('role', ['mentor', 'super_admin']);
      if (error) throw error;
      // Deduplicate by user_id (a user may have both roles)
      const seen = new Set<string>();
      return (data || [])
        .filter((r: any) => {
          const uid = r.profiles?.id || r.user_id;
          if (seen.has(uid)) return false;
          seen.add(uid);
          return true;
        })
        .map((r: any) => ({
          id: r.profiles?.id || r.user_id,
          full_name: r.profiles?.full_name || '',
          email: r.profiles?.email || '',
          avatar_url: r.profiles?.avatar_url || null,
          bio: r.profiles?.bio || null,
          linkedin_url: r.profiles?.linkedin_url || null,
        }));
    },
    enabled: isSuperAdmin || isAdmin || isAuditor,
  });

  // ── Schedule items for a cohort ──
  function useScheduleItems(cohortId: string | null) {
    return useQuery({
      queryKey: ['cohort-management', 'schedule', cohortId],
      queryFn: async (): Promise<ScheduleItemRow[]> => {
        const { data, error } = await supabase
          .from('cohort_schedule_items')
          .select('*')
          .eq('cohort_id', cohortId!)
          .order('week_number', { ascending: true });
        if (error) throw error;
        return data || [];
      },
      enabled: !!cohortId,
    });
  }

  // ── Participants of a cohort ──
  function useCohortParticipants(cohortId: string | null) {
    return useQuery({
      queryKey: ['cohort-management', 'participants', cohortId],
      queryFn: async (): Promise<CohortParticipantRow[]> => {
        const { data, error } = await supabase
          .from('cohort_participants')
          .select('id, cohort_id, user_id, enrolled_at, profiles(full_name, email, area)')
          .eq('cohort_id', cohortId!);
        if (error) throw error;
        return (data || []).map((r: any) => ({
          id: r.id,
          cohort_id: r.cohort_id,
          user_id: r.user_id,
          enrolled_at: r.enrolled_at,
          full_name: r.profiles?.full_name || '',
          email: r.profiles?.email || '',
          area: r.profiles?.area || null,
        }));
      },
      enabled: !!cohortId,
    });
  }

  // ── Available users to assign (profiles from org) ──
  function useAvailableProfiles(orgId: string | null) {
    return useQuery({
      queryKey: ['cohort-management', 'available-profiles', orgId],
      queryFn: async () => {
        // Paginated: sin esto PostgREST corta silenciosamente en 1000 rows,
        // ocultando usuarios (bug reportado 2026-07-30: el modal solo mostraba
        // hasta la letra "G" en cohorte Reto Bolivar IA).
        // Ver [[feedback_postgrest_max_rows]].
        return paginateQuery<{ id: string; full_name: string | null; email: string | null; area: string | null }>(
          (from, to) => {
            let q = supabase
              .from('profiles')
              .select('id, full_name, email, area')
              .order('full_name')
              .range(from, to);
            if (orgId) q = q.eq('organization_id', orgId);
            return q;
          },
        );
      },
      enabled: !!orgId || isSuperAdmin,
    });
  }

  // ── Program workshops (for schedule generation) ──
  function useProgramWorkshops(programId: string | null) {
    return useQuery({
      queryKey: ['cohort-management', 'program-workshops', programId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('program_workshops')
          .select('id, title, week_number, sort_order')
          .eq('program_id', programId!)
          .is('deleted_at', null)
          .order('week_number', { ascending: true });
        if (error) throw error;
        return data || [];
      },
      enabled: !!programId,
    });
  }

  // ── Fetch workshops on-demand (for create flow) ──
  const fetchProgramWorkshops = async (programId: string) => {
    const { data, error } = await supabase
      .from('program_workshops')
      .select('id, title, week_number, sort_order')
      .eq('program_id', programId)
      .is('deleted_at', null)
      .order('week_number', { ascending: true });
    if (error) throw error;
    return data || [];
  };

  // ── Mutations ──

  const createCohort = useMutation({
    mutationFn: async (params: {
      name: string;
      program_id: string;
      organization_id: string | null;
      // start_date/end_date son nullable: rutas como "Profesional Líder IA"
      // pueden crearse sin fecha y agendarse más tarde desde edición.
      start_date: string | null;
      end_date: string | null;
      demo_day_date?: string;
      demo_day_start_hour?: number;
      demo_day_end_hour?: number;
      is_public?: boolean;
      status?: string;
      session_periodicity_days?: number;
      week_date_overrides?: Record<number, string | null>; // weekNumber → 'YYYY-MM-DD' o null para "sin fecha explícito"
    }) => {
      const { data, error } = await supabase
        .from('cohorts')
        .insert({
          name: params.name,
          program_id: params.program_id,
          organization_id: params.organization_id,
          start_date: params.start_date,
          end_date: params.end_date,
          demo_day_date: params.demo_day_date || null,
          demo_day_start_hour: params.demo_day_start_hour ?? null,
          demo_day_end_hour: params.demo_day_end_hour ?? null,
          is_public: params.is_public ?? false,
          status: (params.status || 'draft') as any,
          // Semana N = inicio + N × periodicidad (1ª sesión una periodicidad tras el inicio).
          first_session_after_start: true,
        } as any)
        .select()
        .single();
      if (error) throw error;

      // Auto-generate schedule from program workshops. Las mentorías NO se
      // heredan de la ruta: cada cohorte crea las suyas (la ruta solo define
      // si las contempla, programs.has_mentorships).
      try {
        const workshops = await fetchProgramWorkshops(params.program_id);
        const rows: any[] = [];
        workshops.forEach(ws => {
          rows.push({
            cohort_id: data.id,
            type: 'workshop',
            ref_id: ws.id,
            title: ws.title,
            week_number: ws.week_number,
            start_hour: 14,
            start_minute: 0,
            end_hour: 18,
            end_minute: 0,
          });
        });
        // Asignar scheduled_date a cada sesión:
        //   1. Si hay override de semana → usarlo (puede ser null).
        //   2. Si hay periodicidad → start_date + semana × periodicidad (la 1ª
        //      sesión cae una periodicidad después del inicio).
        //   3. Si nada aplica → sin fecha.
        const periodicity = params.session_periodicity_days;
        const overrides = params.week_date_overrides || {};
        rows.forEach(row => {
          const wn = row.week_number;
          if (typeof wn !== 'number' || wn < 1) return;
          if (wn in overrides) {
            row.scheduled_date = overrides[wn];
          } else if (periodicity && params.start_date) {
            const d = new Date(params.start_date + 'T12:00:00');
            d.setDate(d.getDate() + wn * periodicity);
            row.scheduled_date = d.toISOString().split('T')[0];
          }
        });

        if (rows.length > 0) {
          await supabase.from('cohort_schedule_items').insert(rows);
        }
      } catch {
        // Non-blocking: schedule generation failed but cohort was created
      }

      return data;
    },
    onSuccess: () => {
      invalidateAllCohortConsumers();
    },
  });

  const updateCohort = useMutation({
    mutationFn: async (params: {
      id: string;
      updates: Record<string, any>;
    }) => {
      const { error } = await supabase
        .from('cohorts')
        .update(params.updates)
        .eq('id', params.id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAllCohortConsumers();
    },
  });

  const deleteCohort = useMutation({
    mutationFn: async (cohortId: string) => {
      // Obtener el anchor event del Meet compartido antes de borrar
      const { data: cohortRow } = await supabase
        .from('cohorts')
        .select('meet_anchor_event_id')
        .eq('id', cohortId)
        .maybeSingle();

      // Borrar eventos del calendario (envía emails de cancelación a los invitados)
      await deleteCalendarEventsForScheduleItems({ cohortIds: [cohortId] });

      // Borrar también el anchor event (sala Meet compartida) si existe
      if (cohortRow?.meet_anchor_event_id) {
        try {
          await supabase.functions.invoke('delete-calendar-events', {
            body: { google_event_ids: [cohortRow.meet_anchor_event_id] },
          });
        } catch (e) {
          console.warn('[deleteCohort] anchor event deletion failed', e);
        }
      }

      await supabase.from('cohort_schedule_items').delete().eq('cohort_id', cohortId);
      await supabase.from('cohort_participants').delete().eq('cohort_id', cohortId);
      await supabase.from('cohort_mentors').delete().eq('cohort_id', cohortId);
      const { error } = await supabase.from('cohorts').delete().eq('id', cohortId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAllCohortConsumers();
    },
  });

  const duplicateCohort = useMutation({
    mutationFn: async (sourceCohortId: string) => {
      const source = (cohortsQuery.data || []).find(c => c.id === sourceCohortId);
      if (!source) throw new Error('Cohorte no encontrada');

      // Create new cohort
      const { data: newCohort, error } = await supabase
        .from('cohorts')
        .insert({
          name: `${source.name} (copia)`,
          program_id: source.program_id,
          organization_id: source.organization_id,
          start_date: source.start_date,
          end_date: source.end_date,
          demo_day_date: source.demo_day_date,
          demo_day_start_hour: source.demo_day_start_hour,
          demo_day_end_hour: source.demo_day_end_hour,
          demo_day_link: source.demo_day_link,
          demo_day_enabled: source.demo_day_enabled,
          project_deadline: source.project_deadline,
          program_certificate_id: (source as any).program_certificate_id ?? null,
          is_public: source.is_public,
          price: source.price,
          currency: (source.currency || 'USD') as any,
          enrollment_open: source.enrollment_open,
          status: 'draft' as any,
        })
        .select()
        .single();
      if (error) throw error;

      // Copy schedule items
      const { data: scheduleItems } = await supabase
        .from('cohort_schedule_items')
        .select('*')
        .eq('cohort_id', sourceCohortId);

      if (scheduleItems && scheduleItems.length > 0) {
        // Las grabaciones y datos de asistencia son exclusivos de cada cohorte — no se copian.
        const rows = scheduleItems.map(s => ({
          cohort_id: newCohort.id,
          type: s.type,
          ref_id: s.ref_id,
          title: s.title,
          description: s.description ?? null,
          week_number: s.week_number,
          scheduled_date: s.scheduled_date,
          start_hour: s.start_hour,
          start_minute: s.start_minute,
          end_hour: s.end_hour,
          end_minute: s.end_minute,
          connection_link: s.connection_link,
          expert_id: s.expert_id,
        }));
        await supabase.from('cohort_schedule_items').insert(rows);
      }

      return newCohort;
    },
    onSuccess: () => {
      invalidateAllCohortConsumers();
    },
  });

  const upsertScheduleItems = useMutation({
    mutationFn: async (params: { cohort_id: string; items: Partial<ScheduleItemRow>[] }) => {
      // Diff against existing rows to PRESERVE google_event_id, meet_link,
      // recording_link, fireflies_id, transcript_url, recording_status...
      // A blind delete+insert breaks the Calendar linkage and causes the
      // sync to re-create events instead of updating them.
      const { data: existing, error: fetchErr } = await supabase
        .from('cohort_schedule_items')
        .select('id')
        .eq('cohort_id', params.cohort_id);
      if (fetchErr) throw fetchErr;

      const existingIds = new Set((existing || []).map((r: any) => r.id));
      const incomingIds = new Set(
        params.items.filter(i => (i as any).id).map(i => (i as any).id as string)
      );

      // 1) DELETE rows no longer in the incoming list
      const toDelete = [...existingIds].filter(id => !incomingIds.has(id));
      if (toDelete.length > 0) {
        // Quitar primero su evento de Google Calendar para no dejarlo huérfano
        // en el calendario de los participantes (no bloqueante).
        await deleteCalendarEventsForScheduleItems({ scheduleItemIds: toDelete });
        const { error } = await supabase
          .from('cohort_schedule_items')
          .delete()
          .in('id', toDelete);
        if (error) throw error;
      }

      // 2) UPDATE existing rows — campos editables por el admin.
      //    No se tocan: google_event_id, meet_link, fireflies_id, transcript_url,
      //    recording_status, attendees, attendance_report_link (los maneja el sync automático).
      //    recording_link y meet_recording_link sí se pueden corregir manualmente.
      for (const item of params.items) {
        const id = (item as any).id as string | undefined;
        if (!id || !existingIds.has(id)) continue;
        const { error } = await supabase
          .from('cohort_schedule_items')
          .update({
            type: item.type || null,
            ref_id: item.ref_id || null,
            title: item.title || null,
            description: item.description ?? null,
            week_number: item.week_number ?? null,
            scheduled_date: item.scheduled_date || null,
            start_hour: item.start_hour ?? null,
            start_minute: item.start_minute ?? null,
            end_hour: item.end_hour ?? null,
            end_minute: item.end_minute ?? null,
            connection_link: item.connection_link || null,
            expert_id: item.expert_id || null,
            recording_link: item.recording_link ?? null,
            meet_recording_link: item.meet_recording_link ?? null,
          })
          .eq('id', id);
        if (error) throw error;
      }

      // 3) INSERT brand new rows (no id yet)
      const toInsert = params.items
        .filter(i => !(i as any).id)
        .map(item => ({
          cohort_id: params.cohort_id,
          type: item.type || null,
          ref_id: item.ref_id || null,
          title: item.title || null,
          description: item.description ?? null,
          week_number: item.week_number ?? null,
          scheduled_date: item.scheduled_date || null,
          start_hour: item.start_hour ?? null,
          start_minute: item.start_minute ?? null,
          end_hour: item.end_hour ?? null,
          end_minute: item.end_minute ?? null,
          connection_link: item.connection_link || null,
          expert_id: item.expert_id || null,
        }));
      if (toInsert.length > 0) {
        const { error } = await supabase.from('cohort_schedule_items').insert(toInsert);
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cohort-management', 'schedule', vars.cohort_id] });
    },
  });

  // Recalcula scheduled_date para los cohort_schedule_items que aún no tienen
  // fecha, a partir de start_date + periodicidad (cada N días entre semanas).
  // Respeta los items que ya tienen fecha asignada manualmente.
  const recalculateEmptyScheduleDates = useMutation({
    mutationFn: async (params: { cohort_id: string; start_date: string; periodicity_days: number }) => {
      const { data: items, error: fetchErr } = await supabase
        .from('cohort_schedule_items')
        .select('id, week_number, scheduled_date, type')
        .eq('cohort_id', params.cohort_id);
      if (fetchErr) throw fetchErr;
      // Cohortes nuevas: semana N = inicio + N × periodicidad. Previas: (N − 1).
      const { data: cohortRow, error: cohortErr } = await supabase
        .from('cohorts')
        .select('first_session_after_start')
        .eq('id', params.cohort_id)
        .maybeSingle();
      if (cohortErr) throw cohortErr;
      const weekOffset = (cohortRow as any)?.first_session_after_start ? 0 : 1;

      const updates: Array<{ id: string; scheduled_date: string }> = [];
      (items || []).forEach((row: any) => {
        if (row.scheduled_date) return; // respetar fechas existentes
        // Mentorías se administran manualmente (sin auto-calcular desde week_number)
        if (row.type === 'mentorship') return;
        const wn = row.week_number;
        if (typeof wn !== 'number' || wn < 1) return;
        const d = new Date(params.start_date + 'T12:00:00');
        d.setDate(d.getDate() + (wn - weekOffset) * params.periodicity_days);
        updates.push({ id: row.id, scheduled_date: d.toISOString().split('T')[0] });
      });

      for (const u of updates) {
        const { error } = await supabase
          .from('cohort_schedule_items')
          .update({ scheduled_date: u.scheduled_date })
          .eq('id', u.id);
        if (error) throw error;
      }
      return { updated: updates.length };
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cohort-management', 'schedule', vars.cohort_id] });
      queryClient.invalidateQueries({ queryKey: ['cohort-schedule-items'] });
    },
  });

  const syncParticipants = useMutation({
    mutationFn: async (params: { cohort_id: string; user_ids: string[] }) => {
      const { data: current, error: fetchErr } = await supabase
        .from('cohort_participants')
        .select('user_id')
        .eq('cohort_id', params.cohort_id);
      if (fetchErr) throw fetchErr;
      const currentIds = (current || []).map(r => r.user_id);

      const toAdd = params.user_ids.filter(id => !currentIds.includes(id));
      const toRemove = currentIds.filter(id => !params.user_ids.includes(id));

      if (toRemove.length > 0) {
        const { error } = await supabase
          .from('cohort_participants')
          .delete()
          .eq('cohort_id', params.cohort_id)
          .in('user_id', toRemove);
        if (error) throw error;
      }
      if (toAdd.length > 0) {
        const { error } = await supabase
          .from('cohort_participants')
          .insert(toAdd.map(uid => ({ cohort_id: params.cohort_id, user_id: uid })));
        if (error) throw error;
      }
      return { changed: toAdd.length > 0 || toRemove.length > 0 };
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cohort-management', 'participants', vars.cohort_id] });
      invalidateAllCohortConsumers();
    },
  });

  // ── Mentors of a cohort ──
  function useCohortMentors(cohortId: string | null) {
    return useQuery({
      queryKey: ['cohort-management', 'mentors', cohortId],
      queryFn: async (): Promise<CohortMentorRow[]> => {
        const { data, error } = await supabase
          .from('cohort_mentors')
          .select('id, cohort_id, user_id, profiles(full_name, email)')
          .eq('cohort_id', cohortId!);
        if (error) throw error;
        return (data || []).map((r: any) => ({
          id: r.id,
          cohort_id: r.cohort_id,
          user_id: r.user_id,
          full_name: r.profiles?.full_name || '',
          email: r.profiles?.email || '',
        }));
      },
      enabled: !!cohortId,
    });
  }

  // ── Available mentors (users with mentor role) ──
  function useAvailableMentors() {
    return useQuery({
      queryKey: ['cohort-management', 'available-mentors'],
      queryFn: async () => {
        // Paginated para evitar cap 1000 de PostgREST — ver [[feedback_postgrest_max_rows]].
        const rows = await paginateQuery<{ user_id: string; profiles: any }>(
          (from, to) => supabase
            .from('user_roles')
            .select('user_id, profiles(id, full_name, email)')
            .in('role', ['mentor', 'super_admin'])
            .range(from, to),
          undefined,
          'user_id',
        );
        return rows.map((r: any) => ({
          id: r.profiles?.id || r.user_id,
          full_name: r.profiles?.full_name || '',
          email: r.profiles?.email || '',
        }));
      },
      enabled: isSuperAdmin || isAdmin || isAuditor,
    });
  }

  const syncMentors = useMutation({
    mutationFn: async (params: { cohort_id: string; user_ids: string[] }) => {
      const { data: current, error: fetchErr } = await supabase
        .from('cohort_mentors')
        .select('user_id')
        .eq('cohort_id', params.cohort_id);
      if (fetchErr) throw fetchErr;
      const currentIds = (current || []).map(r => r.user_id);

      const toAdd = params.user_ids.filter(id => !currentIds.includes(id));
      const toRemove = currentIds.filter(id => !params.user_ids.includes(id));

      if (toRemove.length > 0) {
        const { error } = await supabase
          .from('cohort_mentors')
          .delete()
          .eq('cohort_id', params.cohort_id)
          .in('user_id', toRemove);
        if (error) throw error;
      }
      if (toAdd.length > 0) {
        const { error } = await supabase
          .from('cohort_mentors')
          .insert(toAdd.map(uid => ({ cohort_id: params.cohort_id, user_id: uid })));
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cohort-management', 'mentors', vars.cohort_id] });
    },
  });

  // ── Auditores de una cohorte (clon del patrón de mentores) ──
  // El rol Auditor (solo lectura) se asigna a cohortes acá. Solo super_admin gestiona.
  function useCohortAuditors(cohortId: string | null) {
    return useQuery({
      queryKey: ['cohort-management', 'auditors', cohortId],
      queryFn: async (): Promise<CohortMentorRow[]> => {
        // cohort_auditors aún no está en types.ts generado (se regenera al correr
        // la migración) → cast a any. Mismo patrón que el RPC resync_cohort_curriculum.
        const { data, error } = await (supabase as any)
          .from('cohort_auditors')
          .select('id, cohort_id, user_id, profiles(full_name, email)')
          .eq('cohort_id', cohortId!);
        if (error) throw error;
        return (data || []).map((r: any) => ({
          id: r.id,
          cohort_id: r.cohort_id,
          user_id: r.user_id,
          full_name: r.profiles?.full_name || '',
          email: r.profiles?.email || '',
        }));
      },
      enabled: !!cohortId,
    });
  }

  // ── Usuarios con rol auditor (candidatos a asignar) ──
  function useAvailableAuditors() {
    return useQuery({
      queryKey: ['cohort-management', 'available-auditors'],
      queryFn: async () => {
        const rows = await paginateQuery<{ user_id: string; profiles: any }>(
          (from, to) => (supabase as any)
            .from('user_roles')
            .select('user_id, profiles(id, full_name, email)')
            .eq('role', 'auditor')
            .range(from, to),
          undefined,
          'user_id',
        );
        return rows.map((r: any) => ({
          id: r.profiles?.id || r.user_id,
          full_name: r.profiles?.full_name || '',
          email: r.profiles?.email || '',
        }));
      },
      enabled: isSuperAdmin,
    });
  }

  const syncAuditors = useMutation({
    mutationFn: async (params: { cohort_id: string; user_ids: string[] }) => {
      const { data: current, error: fetchErr } = await (supabase as any)
        .from('cohort_auditors')
        .select('user_id')
        .eq('cohort_id', params.cohort_id);
      if (fetchErr) throw fetchErr;
      const currentIds = (current || []).map((r: any) => r.user_id);

      const toAdd = params.user_ids.filter(id => !currentIds.includes(id));
      const toRemove = currentIds.filter((id: string) => !params.user_ids.includes(id));

      if (toRemove.length > 0) {
        const { error } = await (supabase as any)
          .from('cohort_auditors')
          .delete()
          .eq('cohort_id', params.cohort_id)
          .in('user_id', toRemove);
        if (error) throw error;
      }
      if (toAdd.length > 0) {
        const { error } = await (supabase as any)
          .from('cohort_auditors')
          .insert(toAdd.map(uid => ({ cohort_id: params.cohort_id, user_id: uid })));
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cohort-management', 'auditors', vars.cohort_id] });
    },
  });

  const importParticipantsByEmail = useMutation({
    mutationFn: async (params: { cohort_id: string; emails: string[] }) => {
      const normalizedEmails = params.emails.map(e => e.trim().toLowerCase()).filter(Boolean);
      if (normalizedEmails.length === 0) throw new Error('No se encontraron correos válidos');

      // Look up profiles by email
      const { data: profiles, error: profilesErr } = await supabase
        .from('profiles')
        .select('id, email')
        .in('email', normalizedEmails);
      if (profilesErr) throw profilesErr;

      const foundEmails = new Set((profiles || []).map(p => p.email?.toLowerCase()));
      const notFound = normalizedEmails.filter(e => !foundEmails.has(e));

      if (!profiles || profiles.length === 0) {
        return { added: 0, notFound: normalizedEmails };
      }

      // Get existing participants
      const { data: existing } = await supabase
        .from('cohort_participants')
        .select('user_id')
        .eq('cohort_id', params.cohort_id);
      const existingIds = new Set((existing || []).map(r => r.user_id));

      const toAdd = profiles.filter(p => !existingIds.has(p.id));
      if (toAdd.length > 0) {
        const { error } = await supabase
          .from('cohort_participants')
          .insert(toAdd.map(p => ({ cohort_id: params.cohort_id, user_id: p.id })));
        if (error) throw error;
      }

      return { added: toAdd.length, notFound };
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cohort-management', 'participants', vars.cohort_id] });
      invalidateAllCohortConsumers();
    },
  });

  // Generate schedule items from program workshops
  const generateScheduleFromWorkshops = (
    workshops: { id: string; title: string; week_number: number | null }[],
    defaultStartHour = 14,
    defaultEndHour = 18
  ): Partial<ScheduleItemRow>[] => {
    return workshops.map(ws => ({
      type: 'workshop',
      ref_id: ws.id,
      title: ws.title,
      week_number: ws.week_number,
      start_hour: defaultStartHour,
      start_minute: 0,
      end_hour: defaultEndHour,
      end_minute: 0,
    }));
  };

  return {
    // Data
    cohorts: cohortsQuery.data || [],
    filteredCohorts,
    programs: programsQuery.data || [],
    experts: expertsQuery.data || [],
    isLoading: cohortsQuery.isLoading,
    // Sub-queries
    useScheduleItems,
    useCohortParticipants,
    useCohortMentors,
    useCohortAuditors,
    useAvailableProfiles,
    useAvailableMentors,
    useAvailableAuditors,
    useProgramWorkshops,
    // Mutations
    createCohort,
    updateCohort,
    deleteCohort,
    duplicateCohort,
    upsertScheduleItems,
    recalculateEmptyScheduleDates,
    syncParticipants,
    syncMentors,
    syncAuditors,
    importParticipantsByEmail,
    
    // Helpers
    generateScheduleFromWorkshops,
    // Role info
    isSuperAdmin,
    isAdmin,
    effectiveOrg,
    showAllOrgs,
    userOrgId,
  };
}
